/**
 * Notification Sound Service for VA Sprouts
 * Lightweight audio notification system optimized for performance
 */

class NotificationSound {
  constructor() {
    this.audioElement = null;
    this.initialized = false;
    this.enabled = true;
    this.volume = 1.0;
    this.notificationSoundPath = '/assets/sounds/new-notification-017-352293.mp3';
    
    // Check if user has previously interacted (survives HMR reloads)
    this.userHasInteracted = this.checkStoredInteraction();
    
    this.loadPreferences();
    this.initializeAudioElement();
    this.setupInteractionListener();
  }

  /**
   * Check if user interaction was previously recorded
   */
  checkStoredInteraction() {
    try {
      return sessionStorage.getItem('va_sprouts_audio_unlocked') === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Store that user has interacted
   */
  storeInteraction() {
    try {
      sessionStorage.setItem('va_sprouts_audio_unlocked', 'true');
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Set up listener for user interaction to enable audio playback
   * Browser autoplay policy requires user interaction before playing audio
   */
  setupInteractionListener() {
    if (typeof window !== 'undefined') {
      // If already interacted, no need to set up listeners
      if (this.userHasInteracted) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationSound] User interaction already recorded');
        }
        return;
      }
      
      const markInteracted = () => {
        this.userHasInteracted = true;
        this.storeInteraction();
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationSound] User interaction detected, audio playback enabled');
        }
        
        // Chrome-specific: Actually try to play a silent sound or load+pause to unlock audio
        // This is crucial for Chrome's autoplay policy
        if (this.audioElement) {
          // Method 1: Load the audio to "prime" it
          this.audioElement.load();
          
          // Method 2: Try play+pause to unlock audio context in Chrome
          // This is a known workaround for Chrome's autoplay restrictions
          const unlockAudio = async () => {
            try {
              // Set volume to 0 temporarily for the unlock attempt
              const originalVolume = this.audioElement.volume;
              this.audioElement.volume = 0;
              this.audioElement.muted = false;
              
              const playPromise = this.audioElement.play();
              if (playPromise !== undefined) {
                await playPromise;
                // Immediately pause after starting
                this.audioElement.pause();
                this.audioElement.currentTime = 0;
              }
              
              // Restore original volume
              this.audioElement.volume = originalVolume;
              
              if (process.env.NODE_ENV === 'development') {
                console.log('[NotificationSound] Chrome audio context unlocked via silent play');
              }
            } catch (err) {
              // Silent fail - this is just to unlock audio
              if (process.env.NODE_ENV === 'development') {
                console.log('[NotificationSound] Audio unlock attempt:', err.name);
              }
            }
          };
          
          // Run unlock asynchronously
          unlockAudio();
        }
      };
      
      // Listen for any user interaction (these fire once each)
      document.addEventListener('click', markInteracted, { once: true });
      document.addEventListener('keydown', markInteracted, { once: true });
      document.addEventListener('touchstart', markInteracted, { once: true });
      document.addEventListener('scroll', markInteracted, { once: true });
      document.addEventListener('mousedown', markInteracted, { once: true });
    }
  }

  initializeAudioElement() {
    if (typeof window !== 'undefined' && !this.audioElement) {
      // eslint-disable-next-line no-undef
      this.audioElement = new Audio();
      this.audioElement.src = this.notificationSoundPath;
      this.audioElement.preload = 'auto';
      this.audioElement.volume = this.volume;
      
      // Chrome-specific: Ensure audio is not muted for autoplay
      this.audioElement.muted = false;
      
      // Chrome-specific: Set crossOrigin if needed for CORS
      this.audioElement.crossOrigin = 'anonymous';
      
      // Add error handler for debugging (only log if src is not empty)
      this.audioElement.addEventListener('error', (e) => {
        // Don't log errors for empty src (happens during cleanup)
        if (e.target.src && e.target.src !== window.location.href) {
          console.error('[NotificationSound] Audio element error:', e.target.error?.code, e.target.error?.message);
        }
      });
      
      // Add canplay handler for debugging
      this.audioElement.addEventListener('canplay', () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationSound] Audio file loaded successfully');
        }
      });
      
      // Add loadedmetadata handler for debugging
      this.audioElement.addEventListener('loadedmetadata', () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationSound] Audio metadata loaded, duration:', this.audioElement.duration);
        }
      });
      
      // Chrome-specific: Add loadeddata handler
      this.audioElement.addEventListener('loadeddata', () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationSound] Audio data loaded, ready state:', this.audioElement.readyState);
        }
      });
    }
  }

  loadPreferences() {
    try {
      const enabled = localStorage.getItem('notificationSoundEnabled');
      const volume = localStorage.getItem('notificationSoundVolume');
      
      this.enabled = enabled !== 'false';
      this.volume = volume ? parseFloat(volume) : 1.0;
      
      if (enabled === null) {
        localStorage.setItem('notificationSoundEnabled', 'true');
      }
      if (volume === null) {
        localStorage.setItem('notificationSoundVolume', '1.0');
      }
    } catch (error) {
      this.enabled = true;
      this.volume = 1.0;
    }
  }

  savePreferences() {
    try {
      localStorage.setItem('notificationSoundEnabled', this.enabled.toString());
      localStorage.setItem('notificationSoundVolume', this.volume.toString());
    } catch (error) {
      // Silently fail - preferences not critical
    }
  }

  initialize() {
    if (this.initialized) return true;
    
    try {
      if (!this.audioElement) {
        this.initializeAudioElement();
      }
      
      if (this.audioElement) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationSound] Initialized successfully, audio src:', this.audioElement.src);
        }
      } else {
        console.error('[NotificationSound] Failed to create audio element');
        return false;
      }
      
      this.initialized = true;
      return true;
    } catch (error) {
      console.error('[NotificationSound] Error during initialization:', error);
      return false;
    }
  }

  async playMessageNotification() {
    if (!this.enabled) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[NotificationSound] Sound disabled, skipping playback');
      }
      return { success: false, reason: 'disabled' };
    }
    
    // Chrome-specific: Check if document is visible (required for autoplay)
    if (typeof document !== 'undefined' && document.hidden) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[NotificationSound] Document is hidden, skipping playback (Chrome autoplay policy)');
      }
      return { success: false, reason: 'document_hidden' };
    }
    
    try {
      if (!this.initialized || !this.audioElement) {
        this.initialize();
      }

      if (!this.audioElement) {
        console.error('[NotificationSound] Audio element not available');
        return { success: false, reason: 'no_audio_element' };
      }

      // Verify audio element has valid src
      const hasValidSrc = this.audioElement.src && 
                          this.audioElement.src.length > 0 && 
                          this.audioElement.src !== window.location.href &&
                          this.audioElement.src.includes('.mp3');
      
      if (!hasValidSrc) {
        // Re-set the source if it's invalid
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationSound] Resetting audio source...');
        }
        this.audioElement.src = this.notificationSoundPath;
        this.audioElement.load();
        // Wait a bit for the audio to load
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Chrome-specific: Ensure audio is not muted
      if (this.audioElement.muted) {
        this.audioElement.muted = false;
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationSound] Unmuted audio element for Chrome compatibility');
        }
      }
      
      // Stop any currently playing audio first
      if (!this.audioElement.paused) {
        this.audioElement.pause();
      }
      
      // Reset to beginning
      this.audioElement.currentTime = 0;
      this.audioElement.volume = this.volume;
      
      // Chrome-specific: Load the audio element again to ensure it's ready
      // This helps with Chrome's stricter autoplay policies
      const isChrome = typeof navigator !== 'undefined' && 
                       /Chrome/.test(navigator.userAgent) && 
                       /Google Inc/.test(navigator.vendor);
      
      if (isChrome && this.audioElement.readyState < 2) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationSound] Chrome detected, ensuring audio is ready...');
        }
        this.audioElement.load();
        // Wait for loadeddata event or timeout
        await Promise.race([
          new Promise(resolve => {
            const handler = () => {
              this.audioElement.removeEventListener('loadeddata', handler);
              resolve();
            };
            this.audioElement.addEventListener('loadeddata', handler);
          }),
          new Promise(resolve => setTimeout(resolve, 500))
        ]);
      }
      
      // Try to play - browser will throw NotAllowedError if user hasn't interacted
      const playPromise = this.audioElement.play();
      
      if (playPromise !== undefined) {
        try {
          await playPromise;
          // Success! Mark that user has interacted
          this.userHasInteracted = true;
          this.storeInteraction();
          console.log('[NotificationSound] ✅ Audio playback started successfully');
          return { success: true };
        } catch (err) {
          // Only log as error in production, not for expected NotAllowedError
          if (err.name === 'NotAllowedError') {
            // This is expected if user hasn't interacted yet - don't spam console
            if (process.env.NODE_ENV === 'development') {
              console.log('[NotificationSound] Waiting for user interaction (NotAllowedError)');
            }
            this.userHasInteracted = false;
            // Clear stored interaction since it was incorrect
            try {
              sessionStorage.removeItem('va_sprouts_audio_unlocked');
            } catch (e) {
              // Ignore - session storage might not be available
            }
            return { success: false, reason: 'no_user_interaction' };
          } else {
            console.error('[NotificationSound] Failed to play audio:', err.name, err.message);
            return { success: false, reason: err.name, message: err.message };
          }
        }
      }
      return { success: true };
    } catch (error) {
      console.error('[NotificationSound] Error during playback:', error);
      return { success: false, reason: 'exception', message: error.message };
    }
  }

  /**
   * Recreate the audio element to recover from stuck states
   */
  recreateAudioElement() {
    try {
      // Remove old element - don't set src to '' as it triggers error
      if (this.audioElement) {
        this.audioElement.pause();
        // Remove event listeners by replacing the element
        this.audioElement.removeAttribute('src');
        this.audioElement.load(); // Reset the element
        this.audioElement = null;
      }
      
      // Create fresh audio element
      // eslint-disable-next-line no-undef
      this.audioElement = new Audio();
      this.audioElement.src = this.notificationSoundPath;
      this.audioElement.preload = 'auto';
      this.audioElement.volume = this.volume;
      
      // Add error handler (only log if src is valid)
      this.audioElement.addEventListener('error', (e) => {
        if (e.target.src && e.target.src.includes('.mp3')) {
          console.error('[NotificationSound] Audio element error:', e.target.error?.code, e.target.error?.message);
        }
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[NotificationSound] Audio element recreated successfully');
      }
    } catch (error) {
      console.error('[NotificationSound] Failed to recreate audio element:', error);
    }
  }

  async playTypingSound() {
    if (!this.enabled || !this.initialized || !this.audioElement) return;
    
    try {
      const originalVolume = this.audioElement.volume;
      this.audioElement.volume = Math.max(0.1, originalVolume * 0.3);
      this.audioElement.currentTime = 0;
      
      const playPromise = this.audioElement.play();
      if (playPromise !== undefined) {
        await playPromise.catch((err) => {
          console.error('[NotificationSound] Failed to play typing sound:', err.name, err.message);
        });
      }
      
      setTimeout(() => {
        if (this.audioElement) {
          this.audioElement.volume = originalVolume;
        }
      }, 100);
    } catch (error) {
      console.error('[NotificationSound] Error during typing sound playback:', error);
    }
  }

  async playSentSound() {
    if (!this.enabled || !this.initialized || !this.audioElement) return;
    
    try {
      const originalVolume = this.audioElement.volume;
      this.audioElement.volume = Math.max(0.2, originalVolume * 0.6);
      this.audioElement.currentTime = 0;
      
      const playPromise = this.audioElement.play();
      if (playPromise !== undefined) {
        await playPromise.catch((err) => {
          console.error('[NotificationSound] Failed to play sent sound:', err.name, err.message);
        });
      }
      
      setTimeout(() => {
        if (this.audioElement) {
          this.audioElement.volume = originalVolume;
        }
      }, 200);
    } catch (error) {
      console.error('[NotificationSound] Error during sent sound playback:', error);
    }
  }

  enable() {
    this.enabled = true;
    this.savePreferences();
  }

  disable() {
    this.enabled = false;
    this.savePreferences();
  }

  isEnabled() {
    return this.enabled;
  }

  toggle() {
    this.enabled ? this.disable() : this.enable();
    return this.enabled;
  }

  setVolume(newVolume) {
    this.volume = Math.max(0, Math.min(1, newVolume));
    if (this.audioElement) {
      this.audioElement.volume = this.volume;
    }
    this.savePreferences();
  }

  getVolume() {
    return this.volume;
  }

  getStatus() {
    return {
      enabled: this.enabled,
      initialized: this.initialized,
      userHasInteracted: this.userHasInteracted,
      volume: this.volume,
      hasAudioElement: !!this.audioElement,
      audioSrc: this.audioElement?.src || null,
      audioPaused: this.audioElement?.paused ?? null,
      audioReadyState: this.audioElement?.readyState ?? null
    };
  }

  /**
   * Force play the notification sound (useful for testing)
   * This bypasses the enabled check but NOT the user interaction check
   */
  async forcePlay() {
    // Force mark as interacted since this is a direct user action
    this.userHasInteracted = true;
    
    try {
      if (!this.audioElement) {
        this.initializeAudioElement();
      }
      
      if (!this.audioElement) {
        console.error('[NotificationSound] Cannot force play - no audio element');
        return { success: false, reason: 'no_audio_element' };
      }
      
      // Ensure valid src
      if (!this.audioElement.src || !this.audioElement.src.includes('.mp3')) {
        this.audioElement.src = this.notificationSoundPath;
        this.audioElement.load();
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Stop any current playback
      if (!this.audioElement.paused) {
        this.audioElement.pause();
      }
      
      this.audioElement.currentTime = 0;
      this.audioElement.volume = this.volume;
      
      await this.audioElement.play();
      console.log('[NotificationSound] ✅ Force play successful');
      return { success: true };
    } catch (error) {
      console.error('[NotificationSound] Force play failed:', error.name, error.message);
      return { success: false, reason: error.name, message: error.message };
    }
  }
}

const notificationSound = new NotificationSound();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.notificationSound = notificationSound;
}

export default notificationSound;
